import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CashDrawersApiService } from '../../cash-drawers/cash-drawers-api.service';
import { RegisterOption } from './register-option';
import { RegisterOptionMapper } from './register-option.mapper';
import { PosOfflineDbService } from '../offline/pos-offline-db.service';
import { OfflineService } from '../offline/offline.service';

@Injectable({
  providedIn: 'root',
})
export class RegisterSelectorService {
  #cashDrawersService = inject(CashDrawersApiService);
  #offlineDbService = inject(PosOfflineDbService);
  #offlineService = inject(OfflineService);

  #registers = signal<RegisterOption[] | null>(null);

  registers = this.#registers.asReadonly();
  #initializationPromise: Promise<void> | null = null;
  loading = signal(false);

  empty = computed(() => {
    const registers = this.registers();
    return !registers || registers.length === 0;
  });

  async init(): Promise<void> {
    if (this.#initializationPromise) {
      await this.#initializationPromise;
      return;
    }

    if (this.#registers() !== null) return;

    this.#initializationPromise = this.loadRegisters();
    try {
      await this.#initializationPromise;
    } finally {
      this.#initializationPromise = null;
    }
  }

  async loadRegisters() {
    try {
      this.loading.set(true);

      if (this.#offlineService.isOffline()) {
        const cachedRegisters = await this.#offlineDbService.getRegisterList();
        if (cachedRegisters && cachedRegisters.length > 0) {
          this.#registers.set(RegisterOptionMapper.toModel(cachedRegisters));
        } else {
          this.#registers.set([]);
        }
        this.loading.set(false);
        return;
      }

      const registers = await this.#cashDrawersService.findRegisterNames();
      this.#registers.set(RegisterOptionMapper.toModel(registers));

      await this.#offlineDbService.saveRegisterList(registers);
    } catch (error) {
      const errorResponse = error as HttpErrorResponse;
      console.error(
        '[Register Selector] Error loading registers:',
        errorResponse.error,
      );

      const cachedRegisters = await this.#offlineDbService.getRegisterList();
      if (cachedRegisters && cachedRegisters.length > 0) {
        this.#registers.set(RegisterOptionMapper.toModel(cachedRegisters));
      } else {
        this.#registers.set([]);
      }
    } finally {
      this.loading.set(false);
    }
  }

  updateRegisterMeta(
    registerId: number,
    min?: string,
    serialNumber?: string,
  ): void {
    this.#registers.update((registers) => {
      if (!registers) return registers;
      return registers.map((r) =>
        r.id === registerId
          ? {
              ...r,
              min: min || r.min,
              serialNumber: serialNumber || r.serialNumber,
            }
          : r,
      );
    });
  }

  reset(): void {
    this.#registers.set(null);
  }
}
